class LegalPolicyNav {
  constructor(section) {
    this.section = section;
    this.nav = section.querySelector('[data-legal-policy-nav]');
    this.body = section.querySelector('.lovoi-legal-policy__body');

    if (!this.nav) return;

    this.pointerDown = false;
    this.hasDragged = false;
    this.startX = 0;
    this.scrollStart = 0;
    this.activePointerId = null;
    this.isLoading = false;

    this.onWheel = this.onWheel.bind(this);
    this.onPointerDown = this.onPointerDown.bind(this);
    this.onPointerMove = this.onPointerMove.bind(this);
    this.onPointerUp = this.onPointerUp.bind(this);
    this.onPointerCancel = this.onPointerUp.bind(this);
    this.onNavClick = this.onNavClick.bind(this);
    this.onPopState = this.onPopState.bind(this);

    this.init();
  }

  init() {
    if (this.body && !history.state?.legalPolicyUrl) {
      history.replaceState({ legalPolicyUrl: window.location.href }, '', window.location.href);
    }

    this.nav.addEventListener('click', this.onNavClick);

    if (this.body) {
      window.addEventListener('popstate', this.onPopState);
    }

    if (!this.canScroll()) return;

    this.nav.addEventListener('wheel', this.onWheel, { passive: false });
    this.nav.addEventListener('pointerdown', this.onPointerDown);
    this.nav.addEventListener('pointermove', this.onPointerMove);
    this.nav.addEventListener('pointerup', this.onPointerUp);
    this.nav.addEventListener('pointercancel', this.onPointerCancel);
  }

  canScroll() {
    return this.nav.scrollWidth > this.nav.clientWidth;
  }

  onNavClick(event) {
    if (this.hasDragged || this.isLoading || !this.body) return;

    const link = event.target.closest('.lovoi-legal-policy__nav-link');
    if (!link) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    if (link.origin !== window.location.origin) return;
    if (link.pathname === window.location.pathname) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.loadPolicyPage(link.href);
  }

  onPopState() {
    if (!this.body || this.isLoading) return;

    this.loadPolicyPage(window.location.href, { skipPush: true });
  }

  setActiveLink(url) {
    const pathname = new URL(url, window.location.origin).pathname;
    let activeLink = null;

    this.nav.querySelectorAll('.lovoi-legal-policy__nav-link').forEach((link) => {
      if (link.pathname === pathname) {
        link.setAttribute('aria-current', 'page');
        activeLink = link;
      } else {
        link.removeAttribute('aria-current');
      }
    });

    if (activeLink) {
      activeLink.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'auto' });
    }
  }

  syncScrollAfterLoad() {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  async loadPolicyPage(url, options = {}) {
    if (this.isLoading) return;

    const nextUrl = new URL(url, window.location.origin).href;

    this.isLoading = true;
    this.body.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(nextUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Failed to load policy page');

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextBody = doc.querySelector('.lovoi-legal-policy__body');

      if (!nextBody) {
        window.location.href = nextUrl;
        return;
      }

      this.body.innerHTML = nextBody.innerHTML;
      this.setActiveLink(nextUrl);
      document.title = doc.title;

      if (!options.skipPush) {
        history.pushState({ legalPolicyUrl: nextUrl }, '', nextUrl);
      }

      this.syncScrollAfterLoad();
    } catch (error) {
      window.location.href = nextUrl;
    } finally {
      this.isLoading = false;
      this.body.removeAttribute('aria-busy');
    }
  }

  onWheel(event) {
    if (!this.canScroll()) return;
    if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

    event.preventDefault();
    this.nav.scrollLeft += event.deltaY;
  }

  onPointerDown(event) {
    if (!this.canScroll()) return;
    if (event.button !== 0 && event.pointerType === 'mouse') return;

    this.pointerDown = true;
    this.hasDragged = false;
    this.activePointerId = event.pointerId;
    this.startX = event.clientX;
    this.scrollStart = this.nav.scrollLeft;
  }

  onPointerMove(event) {
    if (!this.pointerDown || event.pointerId !== this.activePointerId) return;

    const deltaX = event.clientX - this.startX;

    if (!this.hasDragged && Math.abs(deltaX) > 6) {
      this.hasDragged = true;
      this.nav.setPointerCapture(event.pointerId);
      this.nav.classList.add('is-dragging');
    }

    if (!this.hasDragged) return;

    event.preventDefault();
    this.nav.scrollLeft = this.scrollStart - deltaX;
  }

  onPointerUp(event) {
    if (!this.pointerDown || event.pointerId !== this.activePointerId) return;

    this.pointerDown = false;
    this.activePointerId = null;
    this.nav.classList.remove('is-dragging');

    if (this.nav.hasPointerCapture(event.pointerId)) {
      this.nav.releasePointerCapture(event.pointerId);
    }

    if (this.hasDragged) {
      const preventClick = (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopImmediatePropagation();
        this.nav.removeEventListener('click', preventClick, true);
      };

      this.nav.addEventListener('click', preventClick, true);
    }

    window.setTimeout(() => {
      this.hasDragged = false;
    }, 0);
  }
}

function initLegalPolicyNav(section) {
  if (!section || section.dataset.legalPolicyNavInit === 'true') return;

  const nav = section.querySelector('[data-legal-policy-nav]');
  if (!nav) return;

  section.dataset.legalPolicyNavInit = 'true';
  new LegalPolicyNav(section);
}

function initAllLegalPolicyNavs(root = document) {
  root.querySelectorAll('.lovoi-legal-policy:not([data-legal-policy-nav-init])').forEach(initLegalPolicyNav);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAllLegalPolicyNavs());
} else {
  initAllLegalPolicyNavs();
}

document.addEventListener('shopify:section:load', (event) => {
  initAllLegalPolicyNavs(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll('.lovoi-legal-policy').forEach((section) => {
    delete section.dataset.legalPolicyNavInit;
  });
});
